import { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Divider, Input, Message, Panel, Stack, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useCloneWbsTemplate,
  useCreateWbsTemplate,
  useCreateWbsTemplateItem,
  useDeleteWbsTemplate,
  useDeleteWbsTemplateItem,
  useReorderWbsTemplateItems,
  useUpdateWbsTemplate,
  useUpdateWbsTemplateItem,
  useWbsTemplateItems,
  useWbsTemplates,
} from '../hooks/useWbs';
import usePermissions from '../hooks/usePermissions';
import './WbsManagerPage.css';

const idsFromItems = (list) =>
  (Array.isArray(list) ? list : []).map((i) => Number(i?.id)).filter((id) => Number.isInteger(id));

const toPositiveIntOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
};

const SortableWbsItem = ({ item, idx, onEditItem, onDeleteItem, canEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: Number(item.id) });

  return (
    <div
      ref={setNodeRef}
      className="ppc-wbs-item"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
      }}
    >
      <div className="ppc-wbs-item__body">
        <div className="ppc-wbs-item__title">
          <span className="ppc-wbs-item__idx" aria-label={`Item ${idx + 1}`} title={`Item ${idx + 1}`}>
            {idx + 1}.
          </span>
          {item.title}
        </div>
        {item?.assumptions ? (
          <div className="ppc-wbs-item__assumptions">{item.assumptions}</div>
        ) : null}
      </div>

      <div className="ppc-wbs-item__actions">
        {canEdit ? (
          <Button className="ppc-wbs-iconbtn" appearance="subtle" size="xs" {...attributes} {...listeners} title="Drag to reorder" aria-label="Drag to reorder">
            <i className="bi bi-grip-vertical" aria-hidden="true" />
          </Button>
        ) : null}
        {canEdit ? (
          <Button className="ppc-wbs-iconbtn" appearance="subtle" size="xs" onClick={() => onEditItem(item)}>
            <i className="bi bi-pencil" aria-hidden="true" />
          </Button>
        ) : null}
        {canEdit ? (
          <Button className="ppc-wbs-iconbtn ppc-wbs-iconbtn--danger" appearance="subtle" color="red" size="xs" onClick={() => onDeleteItem(item)}>
            <i className="bi bi-x-circle" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default function WbsManagerPage() {
  const permissions = usePermissions();
  const canEdit = permissions.can('wbs.edit');
  const { data: templates = [], isLoading: templatesLoading, error: templatesError } = useWbsTemplates();

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const normalizedSelectedTemplateId = useMemo(() => toPositiveIntOrNull(selectedTemplateId), [selectedTemplateId]);

  const selectedTemplate = useMemo(() => {
    if (normalizedSelectedTemplateId === null) return null;
    return (templates || []).find((t) => Number(t?.id) === normalizedSelectedTemplateId) || null;
  }, [templates, normalizedSelectedTemplateId]);

  useEffect(() => {
    if (normalizedSelectedTemplateId !== null) return;
    if (Array.isArray(templates) && templates.length > 0) {
      setSelectedTemplateId(Number(templates[0].id));
    }
  }, [templates, normalizedSelectedTemplateId]);

  const {
    data: items = [],
    isLoading: itemsLoading,
    error: itemsError,
  } = useWbsTemplateItems(normalizedSelectedTemplateId, { enabled: normalizedSelectedTemplateId !== null });

  const createTemplate = useCreateWbsTemplate({
    onSuccess: (t) => {
      toaster.push(
        <Message type="success" showIcon>
          Template created.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      setSelectedTemplateId(Number(t?.id));
    },
  });

  const updateTemplate = useUpdateWbsTemplate({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Template updated.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
    },
  });

  const cloneTemplate = useCloneWbsTemplate({
    onSuccess: (t) => {
      toaster.push(
        <Message type="success" showIcon>
          Template cloned.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      setSelectedTemplateId(Number(t?.id));
    },
  });

  const deleteTemplate = useDeleteWbsTemplate({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Template deleted.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      setSelectedTemplateId(null);
    },
  });

  const createItem = useCreateWbsTemplateItem({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Item added.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    },
  });

  const updateItem = useUpdateWbsTemplateItem({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Item updated.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    },
  });

  const deleteItem = useDeleteWbsTemplateItem({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Item deleted.
        </Message>,
        { placement: 'topEnd', duration: 2000 }
      );
    },
  });

  const reorderItems = useReorderWbsTemplateItems({
    onSuccess: () => {
      toaster.push(
        <Message type="success" showIcon>
          Order saved.
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
    },
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createNotes, setCreateNotes] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState('');

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemTitle, setAddItemTitle] = useState('');
  const [addItemAssumptions, setAddItemAssumptions] = useState('');

  const [confirmDeleteItemOpen, setConfirmDeleteItemOpen] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);

  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [editItemTitle, setEditItemTitle] = useState('');
  const [editItemAssumptions, setEditItemAssumptions] = useState('');
  const [draftItems, setDraftItems] = useState([]);

  const unsavedOrderWarningMessage = 'You have unsaved item order changes. Leave without saving?';
  const previousTemplateIdRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const hasUnsavedOrderChanges = useMemo(() => {
    const draftIds = idsFromItems(draftItems);
    const serverIds = idsFromItems(items);
    if (draftIds.length !== serverIds.length) return false;
    for (let i = 0; i < draftIds.length; i += 1) {
      if (draftIds[i] !== serverIds[i]) return true;
    }
    return false;
  }, [draftItems, items]);

  useEffect(() => {
    const handler = (e) => {
      if (!hasUnsavedOrderChanges) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedOrderChanges]);

  useEffect(() => {
    if (!hasUnsavedOrderChanges) return undefined;

    const handler = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!anchor) return;

      const hrefAttr = anchor.getAttribute('href');
      if (!hrefAttr || hrefAttr.startsWith('#')) return;
      if (anchor.getAttribute('target') === '_blank') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const nextUrl = new URL(anchor.href, window.location.href);
      const currentUrl = new URL(window.location.href);
      const isSameUrl =
        nextUrl.pathname === currentUrl.pathname &&
        nextUrl.search === currentUrl.search &&
        nextUrl.hash === currentUrl.hash;
      if (isSameUrl) return;

      const ok = window.confirm(unsavedOrderWarningMessage);
      if (!ok) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [hasUnsavedOrderChanges, unsavedOrderWarningMessage]);

  useEffect(() => {
    const templateId = normalizedSelectedTemplateId;
    const previousTemplateId = previousTemplateIdRef.current;
    const templateChanged = templateId !== previousTemplateId;
    const serverItems = Array.isArray(items) ? items : [];

    if (templateChanged) {
      setDraftItems((prev) => {
        const prevIds = idsFromItems(prev);
        const nextIds = idsFromItems(serverItems);
        if (prevIds.length === nextIds.length && prevIds.every((id, idx) => id === nextIds[idx])) return prev;
        return serverItems;
      });
      previousTemplateIdRef.current = templateId;
      return;
    }

    setDraftItems((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) {
        const prevIds = idsFromItems(prev);
        const nextIds = idsFromItems(serverItems);
        if (prevIds.length === nextIds.length && prevIds.every((id, idx) => id === nextIds[idx])) return prev;
        return serverItems;
      }
      const byId = new Map(serverItems.map((item) => [Number(item?.id), item]));
      const merged = [];
      const used = new Set();

      for (const prevItem of prev) {
        const id = Number(prevItem?.id);
        if (!Number.isInteger(id)) continue;
        const latest = byId.get(id);
        if (!latest) continue;
        merged.push(latest);
        used.add(id);
      }

      for (const serverItem of serverItems) {
        const id = Number(serverItem?.id);
        if (!Number.isInteger(id) || used.has(id)) continue;
        merged.push(serverItem);
      }

      const prevIds = idsFromItems(prev);
      const mergedIds = idsFromItems(merged);
      if (prevIds.length === mergedIds.length && prevIds.every((id, idx) => id === mergedIds[idx])) {
        return prev;
      }

      return merged;
    });
  }, [items, normalizedSelectedTemplateId]);

  const openCreate = () => {
    setCreateName('');
    setCreateNotes('');
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    const name = String(createName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon>
          Name is required.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      return;
    }
    await createTemplate.mutateAsync({ name, notes: createNotes });
    setCreateOpen(false);
  };

  const openEdit = () => {
    if (!selectedTemplate) return;
    setEditName(selectedTemplate?.name || '');
    setEditNotes(selectedTemplate?.notes || '');
    setEditOpen(true);
  };

  const openClone = () => {
    if (!selectedTemplate) return;
    setCloneName(`${selectedTemplate?.name || ''} (Copy)`);
    setCloneOpen(true);
  };

  const submitClone = async () => {
    if (!selectedTemplate) return;
    const name = String(cloneName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon>
          Name is required.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      return;
    }
    await cloneTemplate.mutateAsync({
      templateId: selectedTemplate.id,
      payload: { name },
    });
    setCloneOpen(false);
  };

  const submitEdit = async () => {
    if (!selectedTemplate) return;
    const name = String(editName || '').trim();
    if (!name) {
      toaster.push(
        <Message type="warning" showIcon>
          Name is required.
        </Message>,
        { placement: 'topEnd', duration: 2500 }
      );
      return;
    }
    await updateTemplate.mutateAsync({ templateId: selectedTemplate.id, payload: { name, notes: editNotes } });
    setEditOpen(false);
  };

  const submitDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    await deleteTemplate.mutateAsync(selectedTemplate.id);
    setConfirmDeleteOpen(false);
  };

  const submitAddItem = async () => {
    if (!selectedTemplate) return;
    const title = String(addItemTitle || '').trim();
    if (!title) return;
    await createItem.mutateAsync({
      templateId: selectedTemplate.id,
      payload: { title, assumptions: String(addItemAssumptions || '').trim() },
    });
    setAddItemTitle('');
    setAddItemAssumptions('');
    setAddItemOpen(false);
  };

  const openEditItem = (item) => {
    const itemId = Number(item?.id);
    if (!Number.isInteger(itemId)) return;
    setEditItemId(itemId);
    setEditItemTitle(item?.title || '');
    setEditItemAssumptions(item?.assumptions || '');
    setEditItemOpen(true);
  };

  const closeEditItem = () => {
    setEditItemOpen(false);
    setEditItemId(null);
    setEditItemTitle('');
    setEditItemAssumptions('');
  };

  const submitEditItem = async () => {
    if (!selectedTemplate) return;
    const itemId = Number(editItemId);
    if (!Number.isInteger(itemId)) return;
    const title = String(editItemTitle || '').trim();
    if (!title) return;
    await updateItem.mutateAsync({
      templateId: selectedTemplate.id,
      itemId,
      payload: {
        title,
        assumptions: String(editItemAssumptions || '').trim(),
      },
    });
    closeEditItem();
  };

  const openDeleteItem = (item) => {
    const itemId = Number(item?.id);
    if (!Number.isInteger(itemId)) return;
    setDeleteItemTarget(item);
    setConfirmDeleteItemOpen(true);
  };

  const submitDeleteItem = async () => {
    if (!selectedTemplate) return;
    const itemId = Number(deleteItemTarget?.id);
    if (!Number.isInteger(itemId)) return;
    await deleteItem.mutateAsync({ templateId: selectedTemplate.id, itemId });
    setConfirmDeleteItemOpen(false);
    setDeleteItemTarget(null);
  };

  const submitSaveOrder = async () => {
    if (!selectedTemplate) return;
    if (!hasUnsavedOrderChanges) return;
    const itemIds = idsFromItems(draftItems);
    await reorderItems.mutateAsync({ templateId: selectedTemplate.id, itemIds });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = Number(active.id);
    const overId = Number(over.id);
    if (!Number.isInteger(activeId) || !Number.isInteger(overId) || activeId === overId) return;

    setDraftItems((prev) => {
      const oldIndex = prev.findIndex((item) => Number(item?.id) === activeId);
      const newIndex = prev.findIndex((item) => Number(item?.id) === overId);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const selectTemplate = (templateId) => {
    if (hasUnsavedOrderChanges) {
      const ok = window.confirm(unsavedOrderWarningMessage);
      if (!ok) return;
    }
    setSelectedTemplateId(Number(templateId));
  };

  return (
    <div className="ppc-page ppc-wbs">
      <Stack className="ppc-wbs__header" justifyContent="space-between" alignItems="center" wrap>
        <h3 className="ppc-wbs__title">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <i className="bi bi-diagram-3" aria-hidden="true" />
            WBS Manager
          </span>
        </h3>
        {canEdit ? (
          <Button className="ppc-wbs-btn ppc-wbs-btn--primary" appearance="primary" onClick={openCreate}>
            <span className="ppc-wbs-btn__inner">
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New template
            </span>
          </Button>
        ) : null}
      </Stack>

      <Divider className="ppc-wbs__divider" />

      {templatesError ? (
        <Message type="error" showIcon>
          Failed to load templates.
        </Message>
      ) : null}

      <div className="ppc-wbs__layout">
        <Panel className="ppc-wbs-panel" bordered header={<span className="ppc-wbs-panel__title">Templates</span>}>
          {templatesLoading ? <div className="ppc-wbs__muted">Loading…</div> : null}
          {Array.isArray(templates) && templates.length === 0 && !templatesLoading ? (
            <div className="ppc-wbs__muted">No templates yet.</div>
          ) : null}

          <div className="ppc-wbs-templates">
            {(templates || []).map((t) => {
              const isActive = Number(t?.id) === Number(selectedTemplateId);
              return (
                <button
                  key={t.id}
                  className={`ppc-wbs-template${isActive ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => selectTemplate(Number(t.id))}
                >
                  <div className="ppc-wbs-template__name">{t.name}</div>
                  {t.notes ? (
                    <div className="ppc-wbs-template__notes">{String(t.notes).slice(0, 90)}</div>
                  ) : (
                    <div className="ppc-wbs-template__empty">No notes</div>
                  )}
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel
          className="ppc-wbs-panel"
          bordered
          header={
            <div className="ppc-wbs-panel__head">
              <span className="ppc-wbs-panel__title">Items</span>
              <div className="ppc-wbs-panel__actions">
                {canEdit ? (
                  <Button className="ppc-wbs-btn" appearance="ghost" size="sm" disabled={!selectedTemplate} onClick={openEdit}>
                    <span className="ppc-wbs-btn__inner">
                      <i className="bi bi-pencil" aria-hidden="true" />
                      Edit
                    </span>
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button className="ppc-wbs-btn" appearance="ghost" size="sm" disabled={!selectedTemplate} onClick={openClone}>
                    <span className="ppc-wbs-btn__inner">
                      <i className="bi bi-copy" aria-hidden="true" />
                      Clone
                    </span>
                  </Button>
                ) : null}
                {permissions.can('wbs.full') ? (
                  <Button
                    className="ppc-wbs-btn ppc-wbs-btn--danger"
                    appearance="ghost"
                    size="sm"
                    color="red"
                    disabled={!selectedTemplate}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <span className="ppc-wbs-btn__inner">
                      <i className="bi bi-trash" aria-hidden="true" />
                      Delete
                    </span>
                  </Button>
                ) : null}
              </div>
            </div>
          }
        >
          {!selectedTemplate ? <div className="ppc-wbs__muted">Select a template to manage its items.</div> : null}

          {selectedTemplate ? (
            <>
              <div className="ppc-wbs-template-meta">
                <div className="ppc-wbs-template-meta__name">{selectedTemplate.name}</div>
                <div className="ppc-wbs-template-meta__notes">{selectedTemplate.notes || '—'}</div>
              </div>

              {itemsError ? (
                <Message type="error" showIcon>
                  Failed to load items.
                </Message>
              ) : null}

              {itemsLoading ? <div className="ppc-wbs__muted">Loading…</div> : null}

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={idsFromItems(draftItems)} strategy={verticalListSortingStrategy}>
                  <div className="ppc-wbs-items-list">
                    {draftItems.map((item, idx) => (
                      <SortableWbsItem
                        key={item.id}
                        item={item}
                        idx={idx}
                        onEditItem={openEditItem}
                        onDeleteItem={openDeleteItem}
                        canEdit={canEdit}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <Divider />

              <div className="ppc-wbs-footer-actions">
                {hasUnsavedOrderChanges ? (
                  <Button className="ppc-wbs-btn ppc-wbs-btn--success" appearance="primary" color="green" onClick={submitSaveOrder} loading={reorderItems.isPending}>
                    Save order
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button className="ppc-wbs-btn ppc-wbs-btn--primary" appearance="primary" onClick={() => {
                    setAddItemTitle('');
                    setAddItemAssumptions('');
                    setAddItemOpen(true);
                  }}>
                    <span className="ppc-wbs-btn__inner">
                      <i className="bi bi-plus-lg" aria-hidden="true" />
                      Add item
                    </span>
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </Panel>
      </div>

      <Modal open={addItemOpen} onClose={() => setAddItemOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Add Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Title</div>
              <Input
                placeholder="e.g. Kickoff meeting"
                value={addItemTitle}
                onChange={setAddItemTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitAddItem();
                }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Assumptions</div>
              <Input
                as="textarea"
                rows={5}
                placeholder="Optional"
                value={addItemAssumptions}
                onChange={setAddItemAssumptions}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setAddItemOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitAddItem} loading={createItem.isPending} disabled={!String(addItemTitle || '').trim()}>
            Add
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={editItemOpen}
        onClose={() => setEditItemOpen(false)}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Edit Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Title</div>
              <Input
                value={editItemTitle}
                onChange={setEditItemTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitEditItem();
                  if (e.key === 'Escape') closeEditItem();
                }}
              />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Assumptions</div>
              <Input as="textarea" rows={5} placeholder="Optional" value={editItemAssumptions} onChange={setEditItemAssumptions} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeEditItem}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitEditItem} loading={updateItem.isPending}>
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={confirmDeleteItemOpen}
        onClose={() => {
          setConfirmDeleteItemOpen(false);
          setDeleteItemTarget(null);
        }}
        size="xs"
      >
        <Modal.Header>
          <Modal.Title>Delete item?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This cannot be undone.
          </Message>
          <div style={{ marginTop: 10, fontSize: 13 }}>
            Delete <b>{deleteItemTarget?.title || 'this item'}</b>?
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              setConfirmDeleteItemOpen(false);
              setDeleteItemTarget(null);
            }}
            disabled={deleteItem.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton variant="danger-solid" onClick={submitDeleteItem} loading={deleteItem.isPending}>
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>New WBS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Name</div>
              <Input value={createName} onChange={setCreateName} />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Notes</div>
              <Input as="textarea" rows={4} value={createNotes} onChange={setCreateNotes} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setCreateOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitCreate} loading={createTemplate.isPending}>
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Edit Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Name</div>
              <Input value={editName} onChange={setEditName} />
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Notes</div>
              <Input as="textarea" rows={4} value={editNotes} onChange={setEditNotes} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setEditOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitEdit} loading={updateTemplate.isPending}>
            Save
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={cloneOpen} onClose={() => setCloneOpen(false)} size="sm">
        <Modal.Header>
          <Modal.Title>Clone Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>New Name</div>
              <Input value={cloneName} onChange={setCloneName} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setCloneOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="primary" onClick={submitClone} loading={cloneTemplate.isPending}>
            Clone
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Delete template?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will delete the template and all its items.
          </Message>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setConfirmDeleteOpen(false)}>
            Cancel
          </NsiButton>
          <NsiButton variant="danger-solid" onClick={submitDeleteTemplate} loading={deleteTemplate.isPending}>
            Delete
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
